import { Injectable } from '@angular/core';
import { Observable, zip } from 'rxjs';
import { LayerInfosService } from 'src/app/shared/service/layer-infos.service';
import { mergeMap, map } from 'rxjs/operators';
import { AgGridListViewService } from '../../../services/ag-grid-list-view.service';

@Injectable({
  providedIn: 'root',
})
export class CsvExportService {
  constructor(private agGridListViewService: AgGridListViewService, private layerInfosService: LayerInfosService) {}

  /**
   * gets data for csv export & and maps the domains
   * @param tabLayerUrl the full Url to get the data from
   * @param whereClause the where clause (default 1=1)
   * @param orderByFields the fields to order by
   * @param outFields the fields to return
   */
  getDataForCSV(tabLayerUrl: string, whereClause: string, orderByFields: string[], outFields: any[]): Observable<any> {
    return this.getFeatures(tabLayerUrl, whereClause, orderByFields, outFields).pipe(
      mergeMap(features => this.mapDomainsOnFeatureList(features, tabLayerUrl))
    );
  }

  /**
   * gets all features from specific layer (makes multiple request due maxRecordCount limitations on service)
   * @param layerUrl the url to get the data from
   * @param whereClause  whereclause
   * @param orderByFields the fields to order by
   * @param outFields the fields to get the response from
   */
  private getFeatures(layerUrl: string, whereClause: string, orderByFields: string[], outFields: string[]) {
    return this.layerInfosService.getLayerInfos(layerUrl).pipe(
      mergeMap(layerDefinition => {
        const calls = this.featureCalls(
          layerDefinition.data.maxRecordCount,
          layerUrl,
          whereClause,
          orderByFields,
          outFields
        );
        return zip(...calls).pipe(
          map((responses: any[]) => [].concat(...responses.map(response => (response = response.features))))
        );
      })
    );
  }

  /**
   * creates and return the calls to the service.
   * due to limitations with maxRecordCount, we have to make several requests.
   * Stack the requests in an array and return it.
   * @param maxRecordCount the number of features returned per request
   * @param layerUrl the layer to get the features from
   * @param whereClause the whereclause
   * @param orderByFields the fields to order the features
   * @param outFields the fields to return
   */
  private featureCalls(
    maxRecordCount: number,
    layerUrl: string,
    whereClause: string,
    orderByFields: string[],
    outFields: string[]
  ) {
    const requestCount = Math.ceil(this.agGridListViewService.totalTableRecords / maxRecordCount);
    let startNum = 0;
    const calls = [];
    const mapExtent = this.agGridListViewService.getMapExtent();
    for (let i = 0; i < requestCount; i++) {
      calls.push(
        this.agGridListViewService.getFeatures(
          layerUrl,
          whereClause,
          startNum,
          maxRecordCount,
          orderByFields,
          outFields,
          mapExtent
        )
      );
      startNum += maxRecordCount;
    }
    return calls;
  }

  /**
   * this function maps the domain to a given array of features
   * @param features the features to map the domains to
   * @param layerUrl the url to get the domains from
   */
  private mapDomainsOnFeatureList(features: any[], layerUrl: string): Observable<any> {
    return this.layerInfosService.getLayerInfos(layerUrl).pipe(
      map(layerInfos => {
        features.forEach((feature, index) => {
          features[index] = this.agGridListViewService.matchFeatureAttributeDomain(layerInfos.data.fields, feature);
        });
        return this.mapFeaturesToArray(features);
      })
    );
  }

  /**
   * converts an array of feature objects to an array of feature arrays
   * @param f
   */
  private mapFeaturesToArray(f: any): any[] {
    const features = [];

    f.forEach(feature => {
      const attributes = [];
      for (const attribute in feature.attributes) {
        if (feature.attributes.hasOwnProperty(attribute)) attributes.push(feature.attributes[attribute]);
      }
      features.push(attributes);
    });
    return features;
  }

  /**
   * generates csv friendly content with encoding
   */
  generateCSVContent(features: string[][]): string {
    const returnVal = `\"${features.map(e => e.join('";"')).join('"\r\n"')}\"`;
    return returnVal;
  }
}
